import { zodResolver } from '@hookform/resolvers/zod';

import { trackEvent } from '@pm/analytics';
import { FormRadioGroup, FormSubmitButton } from '@pm/forms';
import {
  MedicationStatusEnum,
  TreatmentCatalogue,
  useGetTreatmentQuery,
  usePauseTreatmentMutation,
  useStartTreatmentMutation,
  useStopTreatmentMutation,
} from '@pm/graphql';
import {
  ButtonLink,
  Icon,
  InlineMessage,
  InteractiveCard,
  Tag,
  Text,
  useConfirmationModal,
  useToast,
} from '@pm/ui';
import * as Sentry from '@sentry/react';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import { Centered } from '../../../components/Centered/Centered';
import { EmptyState } from '../../../components/EmptyState/EmptyState';
import LayoutWithBackButton from '../../../components/LayoutWithBackButton/LayoutWithBackButton';
import { RouteLayoutWithBackLink } from '../../../components/RouteLayoutWithBackLink/RouteLayoutWithBackLink';
import { LoadingPage } from '../../../pages/LoadingPage';
import { detectUserErrors } from '../../errors/errors';
import { BackButtonModal } from './BackButtonModal';
import { TreatmentChangedModal } from './TreatmentChangedModal';

// The patient app reads treatment status from GetMePatient via
// PatientProfileProvider. GetPatient is provider-app only, so naming it here
// would match no active query and silently skip the refetch.
const REFETCH_PROFILE = { refetchQueries: ['GetMePatient'] };

type PrepTreatmentProps = {
  /**
   * Where restarting sends the patient. The embedded refill flow is named per
   * brand, so the hosting app supplies it rather than core hardcoding it.
   */
  refillFlowRoute: string;
};

export const PrepTreatment = ({ refillFlowRoute }: PrepTreatmentProps) => {
  const { t } = useTranslation('treatments', { keyPrefix: 'PrepTreatment' });
  const navigate = useNavigate();
  const { data, loading, error } = useGetTreatmentQuery({
    variables: { treatmentCatalogue: TreatmentCatalogue.PrepTreatment },
    fetchPolicy: 'cache-and-network',
  });
  const treatment = data?.treatment;
  const prevRoute = '/';
  const { positive, negative } = useToast();
  const [showAdvisoryModal, setShowAdvisoryModal] = useState(false);
  const [pauseTreatment, { loading: pausing }] =
    usePauseTreatmentMutation(REFETCH_PROFILE);
  const [stopTreatment, { loading: stopping }] =
    useStopTreatmentMutation(REFETCH_PROFILE);
  const [startTreatment, { loading: starting }] =
    useStartTreatmentMutation(REFETCH_PROFILE);
  const changing = pausing || stopping || starting;
  const {
    handleState,
    showConfirmationModal,
    handleConfirm,
    handleClose,
    confirm,
  } = useConfirmationModal();

  const shouldShowError = error || !treatment;

  const schema = z.object({
    treatmentPlan: z.nativeEnum(MedicationStatusEnum).optional(),
  });

  const methods = useForm({
    values: { treatmentPlan: treatment?.medicationStatus },
    resolver: zodResolver(schema),
  });

  const selectedTreatmentPlan = methods.watch('treatmentPlan');

  if (loading) {
    return <LoadingPage />;
  }

  if (shouldShowError) {
    return (
      <RouteLayoutWithBackLink backTo={prevRoute}>
        <Centered>
          <EmptyState
            heading={t('Errors.NotFound.Title')}
            description={t('Errors.NotFound.Message')}
          />
        </Centered>
      </RouteLayoutWithBackLink>
    );
  }

  const handleBackButtonClick = async () => {
    if (
      selectedTreatmentPlan &&
      selectedTreatmentPlan !== treatment.medicationStatus
    ) {
      handleState();
      const { confirmed } = await confirm();
      if (confirmed) {
        return methods.handleSubmit((data) =>
          changeImmediately(data.treatmentPlan),
        )();
      }
    }
    navigate(prevRoute);
  };

  const changeImmediately = async (treatmentPlan?: MedicationStatusEnum) => {
    const input = { treatmentCatalogue: TreatmentCatalogue.PrepTreatment };

    try {
      switch (treatmentPlan) {
        case MedicationStatusEnum.Active: {
          const { data } = await startTreatment({ variables: { input } });
          detectUserErrors(data, (res) => res.startTreatment?.errors);
          positive({ content: t('Success') });
          return navigate(refillFlowRoute);
        }
        case MedicationStatusEnum.Paused: {
          const { data } = await pauseTreatment({ variables: { input } });
          detectUserErrors(data, (res) => res.pauseTreatment?.errors);
          return setShowAdvisoryModal(true);
        }
        case MedicationStatusEnum.Stopped: {
          const { data } = await stopTreatment({ variables: { input } });
          detectUserErrors(data, (res) => res.stopTreatment?.errors);
          return setShowAdvisoryModal(true);
        }
      }
    } catch (err) {
      negative({ content: t('Errors.CouldNotChange') });
      Sentry.captureException(err);
    }
  };

  return (
    <LayoutWithBackButton onBackButtonClick={handleBackButtonClick}>
      <div className="space-y-xxxl pb-m">
        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit((data) =>
              changeImmediately(data.treatmentPlan),
            )}
            className="space-y-l"
          >
            <Text as="h1" size="headingS">
              {t('Heading')}
            </Text>
            <div className="space-y-xxxl">
              <FormRadioGroup
                name="treatmentPlan"
                label={<Text size="headingXS">{t('Title')}</Text>}
                RadioComponent={InteractiveCard}
                space="m"
                options={[
                  {
                    title: t('Selector.Active.Title'),
                    trailingTitleContent: treatment.medicationStatus ===
                      MedicationStatusEnum.Active && (
                      <Tag size="small" intent="positive">
                        {t('Selector.CurrentPlan')}
                      </Tag>
                    ),
                    value: MedicationStatusEnum.Active,
                    children: (
                      <Text size="bodyS">{t('Selector.Active.Body')}</Text>
                    ),
                    size: 'small',
                    'aria-label': t('Selector.Active.Title'),
                  },
                  {
                    title: t('Selector.Pause.Title'),
                    trailingTitleContent: treatment.medicationStatus ===
                      MedicationStatusEnum.Paused && (
                      <Tag size="small" intent="positive">
                        {t('Selector.CurrentPlan')}
                      </Tag>
                    ),
                    value: MedicationStatusEnum.Paused,
                    children: (
                      <Text size="bodyS">{t('Selector.Pause.Body')}</Text>
                    ),
                    size: 'small',
                    'aria-label': t('Selector.Pause.Title'),
                  },
                  {
                    title: t('Selector.Stop.Title'),
                    trailingTitleContent: treatment.medicationStatus ===
                      MedicationStatusEnum.Stopped && (
                      <Tag size="small" intent="positive">
                        {t('Selector.CurrentPlan')}
                      </Tag>
                    ),
                    value: MedicationStatusEnum.Stopped,
                    children: (
                      <Text size="bodyS">{t('Selector.Stop.Body')}</Text>
                    ),
                    size: 'small',
                    'aria-label': t('Selector.Stop.Title'),
                  },
                ]}
              />
              <FormSubmitButton
                width="full"
                disabled={
                  changing ||
                  selectedTreatmentPlan === treatment.medicationStatus
                }
              >
                {t('Actions.ChangePlan')}
              </FormSubmitButton>
            </div>
          </form>
        </FormProvider>
        <InlineMessage
          intent="info"
          title={t('PrescriptionRenewals.Heading')}
          instructions={t('PrescriptionRenewals.SupportingText')}
          action={
            <ButtonLink
              size="large"
              intent="tertiary"
              to={t('PrescriptionRenewals.RenewalLink')}
              width="fit"
              external
              onClick={() => {
                trackEvent('Link Clicked', {
                  linkName: 'prescription_renewals',
                  linkText: t('PrescriptionRenewals.Text'),
                  targetPath: t('PrescriptionRenewals.RenewalLink'),
                  currentPath: window.location.pathname,
                });
              }}
              trailingIcon={
                <Icon
                  name="open_in_new"
                  size="small"
                  color="text-content-subdued"
                />
              }
            >
              <Text size="bodyS">{t('PrescriptionRenewals.Text')}</Text>
            </ButtonLink>
          }
        />
      </div>
      <BackButtonModal
        showConfirmationModal={showConfirmationModal}
        handleState={handleState}
        handleClose={handleClose}
        handleConfirm={handleConfirm}
        text={t('Confirmation.TextImmediate')}
      />
      <TreatmentChangedModal open={showAdvisoryModal} />
    </LayoutWithBackButton>
  );
};
